import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Mic, PhoneCall, PhoneOff, Volume2 } from 'lucide-react';

import { Modal } from '@/components/common/Modal';
import { audioApi } from '@/lib/api/audio';
import { API_BASE_URL } from '@/lib/api/client';
import type {
  AudioVoiceInfo,
  RealtimeTestTicketResponse,
  VoiceCallProductProfile,
  VoiceCallProviderMeta,
} from '@/types/system';
import { RealtimeMicCapture, RealtimePcmPlayer } from './realtimeVoiceAudio';

type CallState = 'idle' | 'connecting' | 'active' | 'ending' | 'ended' | 'error';
const CONNECTION_TIMEOUT_MS = 12_000;

interface TranscriptLine {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

interface RealtimeVoiceTestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  product: 'dora' | 'soulmate';
  profile: VoiceCallProductProfile;
  providerMeta: VoiceCallProviderMeta | null;
}

function websocketUrl(path: string): string {
  const base = API_BASE_URL || window.location.origin;
  const url = new URL(path, base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
  const remainder = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
}

function appendAssistantDelta(lines: TranscriptLine[], delta: string): TranscriptLine[] {
  const last = lines[lines.length - 1];
  if (last?.role === 'assistant') {
    return [...lines.slice(0, -1), { ...last, text: `${last.text}${delta}` }].slice(-6);
  }
  const next: TranscriptLine = { id: crypto.randomUUID(), role: 'assistant', text: delta };
  return [...lines, next].slice(-6);
}

export const RealtimeVoiceTestDialog: React.FC<RealtimeVoiceTestDialogProps> = ({
  isOpen,
  onClose,
  product,
  profile,
  providerMeta,
}) => {
  const voices = providerMeta?.voices || [];
  const matchedVoice = product === 'soulmate'
    ? voices.find((voice) => voice.id === 'tongtong')?.id
    : undefined;
  const initialVoice = profile.voice || matchedVoice || voices[0]?.id || '';
  const [selectedVoice, setSelectedVoice] = useState(initialVoice);
  const [state, setState] = useState<CallState>('idle');
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const [activity, setActivity] = useState<'listening' | 'speaking'>('listening');
  const socketRef = useRef<WebSocket | null>(null);
  const micRef = useRef<RealtimeMicCapture | null>(null);
  const playerRef = useRef<RealtimePcmPlayer | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const stateRef = useRef<CallState>('idle');
  const assistantSpeakingRef = useRef(false);
  const clientVadRef = useRef({ speech: false, silenceSince: 0 });
  const connectionTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen) setSelectedVoice(initialVoice);
  }, [initialVoice, isOpen]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (state !== 'active' || startedAtRef.current === null) return undefined;
    const timer = window.setInterval(() => {
      setElapsed((Date.now() - (startedAtRef.current || Date.now())) / 1000);
    }, 250);
    return () => window.clearInterval(timer);
  }, [state]);

  const cleanup = useCallback(async () => {
    if (connectionTimerRef.current !== null) {
      window.clearTimeout(connectionTimerRef.current);
      connectionTimerRef.current = null;
    }
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket && socket.readyState < WebSocket.CLOSING) socket.close(1000);
    const mic = micRef.current;
    micRef.current = null;
    const player = playerRef.current;
    playerRef.current = null;
    await Promise.all([mic?.stop(), player?.stop()]);
    assistantSpeakingRef.current = false;
    clientVadRef.current = { speech: false, silenceSince: 0 };
  }, []);

  useEffect(() => () => { void cleanup(); }, [cleanup]);

  const sendEvent = useCallback((event: Record<string, unknown>) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({
      event_id: crypto.randomUUID(),
      client_timestamp: Date.now(),
      ...event,
    }));
  }, []);

  const handleCapturedFrame = useCallback((frame: { audio: string; rms: number }) => {
    if (startedAtRef.current === null) return;
    sendEvent({ type: 'input_audio_buffer.append', audio: frame.audio });
    if (profile.vad_mode !== 'client_vad') return;

    const now = Date.now();
    const vad = clientVadRef.current;
    if (frame.rms >= 0.015) {
      if (!vad.speech && assistantSpeakingRef.current && profile.interrupt_enabled) {
        sendEvent({ type: 'response.cancel' });
      }
      vad.speech = true;
      vad.silenceSince = 0;
      setActivity('listening');
      return;
    }
    if (!vad.speech) return;
    if (!vad.silenceSince) vad.silenceSince = now;
    if (now - vad.silenceSince >= 600) {
      sendEvent({ type: 'input_audio_buffer.commit' });
      sendEvent({ type: 'response.create' });
      vad.speech = false;
      vad.silenceSince = 0;
    }
  }, [profile.interrupt_enabled, profile.vad_mode, sendEvent]);

  const finishCall = useCallback(async (nextState: 'ended' | 'error', message = '') => {
    if (stateRef.current === 'ended' || stateRef.current === 'error') return;
    stateRef.current = nextState;
    await cleanup();
    setError(message);
    setState(nextState);
  }, [cleanup]);

  const startCall = async () => {
    stateRef.current = 'connecting';
    setState('connecting');
    setError('');
    setElapsed(0);
    setTranscript([]);
    startedAtRef.current = null;
    try {
      if (!providerMeta || voices.length === 0) throw new Error('实时语音元数据未加载，无法开始测试');
      if (!selectedVoice) throw new Error('请选择测试音色');
      if (!voices.some((voice) => voice.id === selectedVoice)) {
        throw new Error('所选测试音色已不在服务商目录中');
      }
      const mic = new RealtimeMicCapture();
      const player = new RealtimePcmPlayer();
      micRef.current = mic;
      playerRef.current = player;
      await player.resume();
      await mic.start(handleCapturedFrame);

      const response = await audioApi.createRealtimeTestTicket({
        product,
        provider: profile.provider,
        model: profile.model,
        voice: selectedVoice,
        vad_mode: profile.vad_mode,
        interrupt_enabled: profile.interrupt_enabled,
      });
      const ticket = response.data as RealtimeTestTicketResponse;
      const socket = new WebSocket(
        websocketUrl(ticket.websocket_path),
        [ticket.protocol, ticket.ticket_protocol],
      );
      socketRef.current = socket;
      connectionTimerRef.current = window.setTimeout(() => {
        if (stateRef.current === 'connecting') {
          void finishCall('error', '实时语音连接超时');
        }
      }, CONNECTION_TIMEOUT_MS);
      socket.onmessage = (message) => {
        let event: Record<string, any>;
        try {
          event = JSON.parse(message.data) as Record<string, any>;
        } catch {
          void finishCall('error', '实时语音服务返回了无效消息');
          return;
        }
        switch (event.type) {
          case 'test.ready':
            if (connectionTimerRef.current !== null) {
              window.clearTimeout(connectionTimerRef.current);
              connectionTimerRef.current = null;
            }
            startedAtRef.current = Date.now();
            stateRef.current = 'active';
            setState('active');
            setActivity('listening');
            break;
          case 'input_audio_buffer.speech_started':
            setActivity('listening');
            break;
          case 'conversation.item.input_audio_transcription.completed':
            if (event.transcript) {
              setTranscript((lines) => {
                const next: TranscriptLine = {
                  id: event.item_id || crypto.randomUUID(),
                  role: 'user',
                  text: event.transcript,
                };
                return [...lines, next].slice(-6);
              });
            }
            break;
          case 'response.created':
          case 'response.audio.delta':
            assistantSpeakingRef.current = true;
            setActivity('speaking');
            if (event.type === 'response.audio.delta' && event.delta) {
              playerRef.current?.enqueue(event.delta, 24000);
            }
            break;
          case 'response.audio_transcript.delta':
            if (event.delta) setTranscript((lines) => appendAssistantDelta(lines, event.delta));
            break;
          case 'response.audio.done':
          case 'response.done':
          case 'response.cancelled':
            assistantSpeakingRef.current = false;
            setActivity('listening');
            break;
          case 'error':
            void finishCall('error', event.error?.message || '实时语音服务返回错误');
            break;
          case 'test.error':
            void finishCall('error', event.message || '实时语音测试失败');
            break;
          case 'test.ended':
            void finishCall('ended');
            break;
          default:
            break;
        }
      };
      socket.onerror = () => { void finishCall('error', '实时语音连接失败'); };
      socket.onclose = (event) => {
        if (stateRef.current === 'connecting' || stateRef.current === 'active') {
          if (event.code === 1000) {
            void finishCall('ended');
          } else {
            void finishCall('error', event.reason || `实时语音连接已关闭 (${event.code})`);
          }
        }
      };
    } catch (caught) {
      await finishCall('error', caught instanceof Error ? caught.message : String(caught));
    }
  };

  const hangUp = async () => {
    stateRef.current = 'ending';
    setState('ending');
    sendEvent({ type: 'test.end' });
    await finishCall('ended');
  };

  const closeDialog = async () => {
    if (state === 'active' || state === 'connecting' || state === 'ending') {
      sendEvent({ type: 'test.end' });
      await cleanup();
    }
    stateRef.current = 'idle';
    setState('idle');
    setError('');
    setTranscript([]);
    onClose();
  };

  const title = product === 'soulmate' ? 'Soulmate 实时语音测试' : 'Dora 实时语音测试';
  const stateLabel = useMemo(() => {
    if (state === 'connecting') return '正在接通';
    if (state === 'ending') return '正在挂断';
    if (state === 'active') return activity === 'speaking' ? '对方正在说话' : '正在聆听';
    if (state === 'ended') return '通话已结束';
    if (state === 'error') return '通话异常';
    return '准备拨打';
  }, [activity, state]);

  return (
    <Modal isOpen={isOpen} onClose={() => { void closeDialog(); }} title={title} size="lg">
      <div className="space-y-5">
        <div className="grid grid-cols-3 gap-3 border-y border-gray-100 py-3 text-center">
          <div>
            <p className="text-[11px] text-gray-400">模型</p>
            <p className="mt-1 truncate text-xs font-medium text-gray-700">{profile.model}</p>
          </div>
          <div>
            <p className="text-[11px] text-gray-400">断句</p>
            <p className="mt-1 text-xs font-medium text-gray-700">{profile.vad_mode}</p>
          </div>
          <div>
            <p className="text-[11px] text-gray-400">时长</p>
            <p className="mt-1 font-mono text-xs font-medium text-gray-700">{formatDuration(elapsed)}</p>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-gray-600">
            {profile.voice_strategy === 'profile_match' ? '本次测试音色' : '固定音色'}
          </label>
          <select
            className="w-full rounded border bg-white px-3 py-2 text-sm"
            value={selectedVoice}
            onChange={(event) => setSelectedVoice(event.target.value)}
            disabled={state === 'active' || state === 'connecting' || state === 'ending'}
          >
            {voices.map((voice: AudioVoiceInfo) => (
              <option key={voice.id} value={voice.id}>{voice.name} · {voice.id}</option>
            ))}
          </select>
          {profile.voice_strategy === 'profile_match' && (
            <p className="text-[11px] text-gray-400">正式通话由 Soulmate 身份匹配；这里只选择测试样本。</p>
          )}
        </div>

        <div className="min-h-36 rounded border border-gray-100 bg-gray-50 px-4 py-3">
          <div className="mb-3 flex items-center justify-center gap-2 text-sm text-gray-600">
            {state === 'connecting' || state === 'ending' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : activity === 'speaking' && state === 'active' ? (
              <Volume2 className="h-4 w-4 text-pink-600" />
            ) : (
              <Mic className="h-4 w-4 text-emerald-600" />
            )}
            <span>{stateLabel}</span>
          </div>
          <div className="space-y-2">
            {transcript.map((line) => (
              <p key={line.id} className={`text-sm ${line.role === 'user' ? 'text-right text-gray-700' : 'text-left text-pink-700'}`}>
                {line.text}
              </p>
            ))}
          </div>
          {error && <p className="mt-3 text-center text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-center">
          {state === 'active' || state === 'connecting' || state === 'ending' ? (
            <button
              type="button"
              onClick={() => { void hangUp(); }}
              disabled={state === 'ending'}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white shadow-sm transition hover:bg-red-700 disabled:opacity-60"
              title="挂断"
              aria-label="挂断"
            >
              <PhoneOff className="h-6 w-6" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { void startCall(); }}
              className="flex items-center gap-2 rounded bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700"
            >
              <PhoneCall className="h-4 w-4" />
              {state === 'ended' || state === 'error' ? '重新拨打' : '拨打测试'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
